import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { workedTodaySeconds, pausedTodaySeconds } from "@/lib/timeClock";
import { toast } from "sonner";
import {
  LayoutDashboard, Users as UsersIcon, Wrench, Package, FileText, LogOut,
  CircuitBoard, UserCog, Settings as SettingsIcon, ClipboardCheck, Receipt, Clock,
  BarChart3, Menu, X, ChevronDown, Play, Pause, PlayCircle, StopCircle,
  KeyRound, Calendar as CalendarIcon, Columns3, ListTodo, Building2,
} from "lucide-react";

const ROLE_LABELS = {
  admin: "Administrador",
  technician: "Técnico",
  reception: "Recepción",
  billing: "Facturación",
  read_only: "Solo lectura",
};

const STATE_META = {
  sin_iniciar: { label: "Sin fichar",          color: "bg-[#C0C0BA]", ring: "ring-[#C0C0BA]" },
  trabajando:  { label: "Trabajando",          color: "bg-[#6E8B3D]", ring: "ring-[#6E8B3D]" },
  en_pausa:    { label: "En pausa",            color: "bg-amber-500", ring: "ring-amber-500" },
  finalizado:  { label: "Jornada finalizada",  color: "bg-[#E5E5E0]", ring: "ring-[#E5E5E0]" },
};

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [companyLogo, setCompanyLogo] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setCompanyLogo(r.data?.company?.logo_data_b64 || ""))
      .catch((err) => console.warn("[Layout] no se pudo cargar settings:", err));
    // Iter 103 — Versión del software mostrada bajo "Software de reparaciones".
    api.get("/version")
      .then((r) => setAppVersion(r.data?.version || ""))
      .catch(() => {});
  }, []);

  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard", show: true },
    // Iter 177 — "Reparaciones" oculto del menú (ruta /repairs sigue accesible).
    { to: "/repairs", label: "Reparaciones", icon: Wrench, id: "nav-repairs", show: false },
    { to: "/my-repairs", label: "Mis reparaciones", icon: ClipboardCheck, id: "nav-my-repairs", show: true },
    { to: "/customers", label: "Clientes", icon: UsersIcon, id: "nav-customers", show: true },
    { to: "/inventory", label: "Inventario", icon: Package, id: "nav-inventory", show: true },
    { to: "/quotes", label: "Cotizaciones", icon: FileText, id: "nav-quotes", show: false },
    { to: "/billing", label: "Facturación", icon: Receipt, id: "nav-billing", show: hasRole("admin", "billing", "read_only") },
    { to: "/reports", label: "Informes", icon: BarChart3, id: "nav-reports", show: hasRole("admin") },
    { to: "/calendar", label: "Calendario", icon: CalendarIcon, id: "nav-calendar", show: true },
    // Iter 119 — El acceso visible "Planificador" se sustituye por "ToDoList".
    // La ruta /planner y su código NO se eliminan — quedan accesibles por URL
    // directa para poder reactivar en el futuro.
    { to: "/todolist", label: "ToDoList", icon: ListTodo, id: "nav-todolist", show: true },
    // { to: "/planner", label: "Planificador", icon: Columns3, id: "nav-planner", show: hasRole("admin", "reception", "billing") }, // Oculto iter 119
    { to: "/users", label: "Usuarios", icon: UserCog, id: "nav-users", show: hasRole("admin") },
    { to: "/b2b-admin", label: "B2B", icon: Building2, id: "nav-b2b", show: hasRole("admin", "reception", "billing") },
    { to: "/settings", label: "Configuración", icon: SettingsIcon, id: "nav-settings", show: hasRole("admin") },
  ].filter((x) => x.show);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F7F7F4] text-[#111110]">
      {/* ============ Top bar fija ============ */}
      <header
        className="fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-[#E5E5E0] shadow-sm flex items-center"
        data-testid="topbar"
      >
        <div className="flex items-center gap-3 w-full px-3 md:px-5">
          {/* Hamburguesa móvil */}
          <button
            className="md:hidden p-2 hover:bg-[#F7F7F4]"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Abrir menú"
          >
            <Menu size={20} />
          </button>

          {/* Logo + nombre */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="topbar-brand">
            {companyLogo ? (
              <img src={companyLogo} alt="Logo" className="w-8 h-8 object-contain" data-testid="brand-logo" />
            ) : (
              <div className="w-8 h-8 bg-[#111110] text-[#6E8B3D] flex items-center justify-center">
                <CircuitBoard size={18} strokeWidth={2.2} />
              </div>
            )}
            <div className="hidden sm:block min-w-0">
              <div className="font-display font-black text-xs leading-none" data-testid="brand-name">
                iRepairs.es
              </div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#6E8B3D] mt-0.5 leading-none">
                Software de reparaciones
              </div>
              {appVersion && (
                <div
                  className="font-mono text-[9px] uppercase tracking-widest text-[#6E8B3D] mt-0.5 leading-none"
                  data-testid="brand-version"
                >
                  Versión v{appVersion}
                </div>
              )}
            </div>
          </Link>

          {/* Nav central (desktop) */}
          <nav className="hidden md:flex flex-1 items-center justify-center min-w-0 gap-0.5 overflow-x-auto no-scrollbar">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.id}
                className={({ isActive }) =>
                  `relative inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-[#6E8B3D] bg-[#F0F4E8]"
                      : "text-[#111110] hover:bg-[#F7F7F4]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <it.icon size={14} />
                    {it.label}
                    {isActive && (
                      <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#6E8B3D]" aria-hidden="true" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Spacer móvil — el nav está en el drawer */}
          <div className="flex-1 md:hidden" />

          {/* Derecha: Clock widget + user */}
          <div className="flex items-center gap-2 shrink-0">
            <ClockWidget />
            <UserMenu user={user} logout={doLogout} />
          </div>
        </div>
      </header>

      {/* ============ Drawer móvil ============ */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-drawer-overlay">
          <div className="absolute inset-0 bg-black/40" />
          <aside
            className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
              <div className="font-display font-bold text-sm">Menú</div>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 hover:bg-[#F7F7F4]" data-testid="mobile-menu-close">
                <X size={16} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-2">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-${it.id}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium ${
                      isActive ? "bg-[#F0F4E8] text-[#6E8B3D] border-l-2 border-[#6E8B3D]" : "hover:bg-[#F7F7F4]"
                    }`
                  }
                >
                  <it.icon size={16} />
                  {it.label}
                </NavLink>
              ))}
              {/* Enlace adicional a Control horario en el drawer (en desktop está el widget) */}
              <NavLink
                to="/time-clock"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-time-clock"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-[#F0F4E8] text-[#6E8B3D] border-l-2 border-[#6E8B3D]" : "hover:bg-[#F7F7F4]"
                  }`
                }
              >
                <Clock size={16} /> Control horario
              </NavLink>
            </nav>
          </aside>
        </div>
      )}

      {/* ============ Main ============ */}
      <main className="pt-14 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

// =============================================================
// Widget Control horario
// =============================================================

function formatHM(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
}

function ClockWidget() {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0); // re-render para cronómetro local
  const popRef = useRef(null);

  // Polling cada 30 s
  const refresh = async () => {
    try {
      const r = await api.get("/time-clock/today");
      setSummary(r.data);
    } catch (err) {
      // Silencioso: el polling se reintenta cada 30 s.
      console.warn("[ClockWidget] /time-clock/today falló:", err?.message || err);
    }
  };
  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 30000);
    return () => clearInterval(i);
  }, []);

  // Cronómetro local 60 s
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(i);
  }, []);

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const state = summary?.state || "sin_iniciar";
  const meta = STATE_META[state];

  // Cronómetro vivo unificado: usa exactamente la misma fuente que la página
  // /time-clock para que ambos valores siempre coincidan.
  // Ver `lib/timeClock.js` para los detalles del cálculo.
  const liveSeconds = workedTodaySeconds(summary);
  const breakLive = pausedTodaySeconds(summary);

  // forzar render por tick para HH:MM (sin warning de variable no usada)
  void tick;

  const display = state === "trabajando"
    ? `Trabajando · ${formatHM(liveSeconds)}`
    : state === "en_pausa"
    ? `En pausa · ${formatHM(breakLive)}`
    : state === "finalizado"
    ? "Jornada finalizada"
    : "Sin fichar";

  const doAction = async (endpoint, msg) => {
    setBusy(true);
    try {
      await api.post(`/time-clock/${endpoint}`);
      toast.success(msg);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="clock-widget"
        title="Control horario"
        className="inline-flex items-center gap-2 px-2.5 md:px-3 py-1.5 text-xs md:text-sm border border-[#E5E5E0] hover:border-[#111110] bg-white"
      >
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${meta.color} ring-2 ${meta.ring} ring-offset-1`} aria-hidden="true" />
        <span className="hidden sm:inline font-mono">{display}</span>
        <ChevronDown size={12} className="text-[#525252]" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-white border border-[#111110] shadow-hard z-50"
          data-testid="clock-popover"
        >
          <div className="px-3 py-2.5 border-b border-[#E5E5E0]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">// control horario</div>
            <div className="mt-0.5 text-sm font-semibold flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${meta.color}`} aria-hidden="true" />
              {meta.label}
            </div>
            <div className="mt-1 font-mono text-xs text-[#525252]">
              Trabajado hoy: <span className="font-bold text-[#111110]">{formatHM(liveSeconds)}</span>
            </div>
            {Number(summary?.total_pausa_s || 0) > 0 && (
              <div className="font-mono text-[10px] text-[#525252] mt-0.5">
                Pausa total: {formatHM(breakLive)}
              </div>
            )}
          </div>

          {/* Acciones contextuales */}
          <div className="p-2 grid grid-cols-2 gap-1.5">
            {state === "sin_iniciar" && (
              <ClockBtn icon={Play} label="Fichar entrada" onClick={() => doAction("clock-in", "Entrada registrada")} busy={busy} testid="clock-action-in" full primary />
            )}
            {state === "trabajando" && (
              <>
                <ClockBtn icon={Pause} label="Iniciar pausa" onClick={() => doAction("start-break", "Pausa iniciada")} busy={busy} testid="clock-action-pause" />
                <ClockBtn icon={StopCircle} label="Fichar salida" onClick={() => doAction("clock-out", "Salida registrada")} busy={busy} testid="clock-action-out" primary />
              </>
            )}
            {state === "en_pausa" && (
              <ClockBtn icon={PlayCircle} label="Finalizar pausa" onClick={() => doAction("end-break", "Pausa finalizada")} busy={busy} testid="clock-action-resume" full primary />
            )}
            {state === "finalizado" && (
              <div className="col-span-2 text-xs text-[#525252] text-center py-2">
                Solo un administrador puede modificar una jornada finalizada.
              </div>
            )}
          </div>

          <Link
            to="/time-clock"
            onClick={() => setOpen(false)}
            data-testid="clock-open-module"
            className="block px-3 py-2 border-t border-[#E5E5E0] text-center text-[11px] font-mono uppercase tracking-widest text-[#525252] hover:bg-[#F7F7F4]"
          >
            Ver módulo completo →
          </Link>
        </div>
      )}
    </div>
  );
}

function ClockBtn({ icon: Icon, label, onClick, busy, testid, full, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`${full ? "col-span-2" : ""} inline-flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium border disabled:opacity-50 ${
        primary
          ? "bg-[#6E8B3D] hover:bg-[#556C2E] text-white border-[#6E8B3D]"
          : "bg-white hover:bg-[#F7F7F4] border-[#111110] text-[#111110]"
      }`}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

// Las funciones `lastResumeAt` y `lastBreakStartAt` se eliminaron al unificar
// el cálculo del reloj vivo en `lib/timeClock.js`. Ya no son necesarias.

// =============================================================
// User dropdown
// =============================================================

function UserMenu({ user, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const rolesText = (user?.roles || (user?.role ? [user.role] : []))
    .map((r) => ROLE_LABELS[r] || r)
    .join(" · ");

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-trigger"
        className="inline-flex items-center gap-2 px-2 py-1 hover:bg-[#F7F7F4] border border-transparent"
      >
        <span className="inline-flex items-center justify-center w-7 h-7 bg-[#111110] text-white text-[10px] font-bold font-mono">
          {initials}
        </span>
        <span className="hidden md:flex items-center leading-tight">
          <span className="text-xs font-semibold" data-testid="user-name">{user?.name}</span>
        </span>
        <ChevronDown size={12} className="text-[#525252]" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-60 bg-white border border-[#111110] shadow-hard z-50" data-testid="user-menu-dropdown">
          <div className="px-3 py-2.5 border-b border-[#E5E5E0]">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-[#525252] truncate font-mono">{user?.email}</div>
            <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-[#6E8B3D]">
              {rolesText || "usuario"}
            </div>
          </div>
          <Link
            to="/portal"
            onClick={() => setOpen(false)}
            data-testid="user-menu-portal"
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F7F7F4]"
          >
            <UserCog size={14} /> Mi portal
          </Link>
          <Link
            to="/change-password"
            onClick={() => setOpen(false)}
            data-testid="user-menu-change-password"
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F7F7F4]"
          >
            <KeyRound size={14} /> Cambiar contraseña
          </Link>
          <button
            data-testid="logout-button"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F7F7F4] border-t border-[#E5E5E0] text-left"
          >
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
